/**
 * User Roles Constants
 * Define all available roles in the system
 */

export const ROLES = {
  USER: "User",
  ADMIN: "Admin",
  MANAGER: "Manager",
  COURIER: "Courier",
  NUTRITIONIST: "Nutritionist",
};

/**
 * Role Hierarchy (higher number = more permissions)
 * Useful for permission checks like "Admin and above"
 */
export const ROLE_HIERARCHY = {
  [ROLES.USER]: 1,
  [ROLES.COURIER]: 2,
  [ROLES.NUTRITIONIST]: 3,
  [ROLES.MANAGER]: 4,
  [ROLES.ADMIN]: 5,
};

/**
 * Role Labels for Display
 */
export const ROLE_LABELS = {
  [ROLES.USER]: "User",
  [ROLES.ADMIN]: "Admin",
  [ROLES.MANAGER]: "Manager",
  [ROLES.COURIER]: "Courier",
  [ROLES.NUTRITIONIST]: "Nutritionist",
};

/**
 * Role-based Route Access Configuration
 * Define which roles can access which routes/sections
 */
export const ROUTE_PERMISSIONS = {
  // Settings - Admin and Manager only
  settings: [ROLES.ADMIN, ROLES.MANAGER, ROLES.NUTRITIONIST],

  // Dishes - Admin, Manager, and Nutritionist
  dishes: [ROLES.ADMIN, ROLES.MANAGER, ROLES.NUTRITIONIST],

  // Menu - Admin, Manager, and Nutritionist
  menu: [ROLES.ADMIN, ROLES.MANAGER, ROLES.NUTRITIONIST],

  // Orders - All except regular User
  orders: [ROLES.ADMIN, ROLES.MANAGER],

  // Clients - Admin and Manager only
  clients: [ROLES.ADMIN, ROLES.MANAGER],

  // Statistics - Admin and Manager only
  statistics: [ROLES.ADMIN, ROLES.MANAGER],

  // Messages - All roles
  messages: [ROLES.ADMIN, ROLES.MANAGER],

  // Deliveries - Courier, Manager, and Admin
  deliveries: [ROLES.ADMIN, ROLES.MANAGER, ROLES.COURIER],
};

/**
 * Helper function to check if a role has access to a route
 * @param {string} userRole - The user's role
 * @param {string} routeKey - The route key from ROUTE_PERMISSIONS
 * @returns {boolean} - Whether the user has access
 */
export const hasRouteAccess = (userRole, routeKey) => {
  if (!userRole || !routeKey) return false;

  const allowedRoles = ROUTE_PERMISSIONS[routeKey];
  if (!allowedRoles) return false;

  return allowedRoles.includes(userRole);
};

/**
 * Helper function to check if a role is at or above a certain level
 * @param {string} userRole - The user's role
 * @param {string} minimumRole - The minimum required role
 * @returns {boolean} - Whether the user meets the minimum role requirement
 */
export const hasMinimumRole = (userRole, minimumRole) => {
  if (!userRole || !minimumRole) return false;

  const userLevel = ROLE_HIERARCHY[userRole] || 0;
  const minLevel = ROLE_HIERARCHY[minimumRole] || 0;

  return userLevel >= minLevel;
};

/**
 * Get all roles as an array
 */
export const getAllRoles = () => Object.values(ROLES);

/**
 * Validate if a role exists
 */
export const isValidRole = (role) => {
  return getAllRoles().includes(role);
};
